import "@chatscope/chat-ui-kit-styles/dist/default/styles.min.css";
import "./CollaborationChatMessages.scss";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    collection,
    addDoc,
    query,
    orderBy,
    onSnapshot,
    serverTimestamp,
    where,
    getDocs,
    doc,
    updateDoc,
    increment,
} from "firebase/firestore";
import {
    MainContainer,
    ChatContainer,
    MessageList,
    Message,
    MessageInput,
    Avatar,
} from "@chatscope/chat-ui-kit-react";
import moment from "moment";
import DOMPurify from "dompurify";
import { useFirebase } from "contexts/FirebaseAuthContext";
import { Message as MessageInterface, EntityChatRoom } from "types/chat";
import { sanitizeURLs } from "utils/helpers";
import userDummyImg from "assets/images/users/user-dummy-img.jpg";

export const CHAT_ALLOWED_TAGS = [
    "b", "i", "u", "strong", "em", "br",
    "ul", "ol", "li",
    "h1", "h2", "h3", "h4", "h5", "h6",
    "p", "a",
];

export const CHAT_ALLOWED_ATTR = ["href", "rel", "target"];

const MARK_AS_READ_DELAY_MS = 1500;
const SCROLL_BOTTOM_THRESHOLD_PX = 48;

const findMessageListScrollContainer = (root: HTMLElement | null): HTMLElement | null => {
    if (!root) return null;

    const messageList = root.querySelector(".cs-message-list") as HTMLElement | null;
    if (messageList) return messageList;

    const queue: HTMLElement[] = [root];
    while (queue.length > 0) {
        const el = queue.shift()!;
        const { overflowY } = getComputedStyle(el);
        if (
            (overflowY === "auto" || overflowY === "scroll") &&
            el.scrollHeight > el.clientHeight
        ) {
            return el;
        }
        queue.push(...(Array.from(el.children) as HTMLElement[]));
    }

    return null;
};

const isScrolledToBottom = (container: HTMLElement) => {
    const distanceFromBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight;
    return distanceFromBottom <= SCROLL_BOTTOM_THRESHOLD_PX;
};

export const getMessageGroupKey = (
    msg: { senderId: string; senderEntityId?: string },
    currentEntityId: string | null | undefined,
    influencerUuid: string | undefined
): string => {
    if (influencerUuid && msg.senderEntityId === influencerUuid) {
        return `outgoing-influencer:${msg.senderId}`;
    }
    if (currentEntityId && msg.senderEntityId === currentEntityId) {
        return `outgoing-hub:${msg.senderId}`;
    }
    return `incoming:${msg.senderId}`;
};

const getGroupKey = (msg: { __groupKey?: string; senderId: string }) =>
    msg.__groupKey ?? msg.senderId;

export const getMessageDateKey = (msg: { timestamp?: unknown }) => {
    const date = parseFirestoreTimestamp(msg.timestamp) ?? new Date();
    return moment(date).startOf("day").toISOString();
};

export const getMessagePosition = (currentMsg: any, lastMsg: any, nextMsg: any) => {
    const currentKey = getGroupKey(currentMsg);
    const lastKey = lastMsg ? getGroupKey(lastMsg) : null;
    const nextKey = nextMsg ? getGroupKey(nextMsg) : null;

    if ((!lastKey || lastKey !== currentKey) && (!nextKey || nextKey !== currentKey)) {
        return "single";
    }
    if ((!lastKey || lastKey !== currentKey) && nextKey === currentKey) {
        return "first";
    }
    if (lastKey === currentKey && (!nextKey || nextKey !== currentKey)) {
        return "last";
    }
    if (lastKey === currentKey && nextKey === currentKey) {
        return "normal";
    }
    return "single";
};

export const groupMessagesByDate = (messages: MessageInterface[]) => {
    const groups: Record<string, MessageInterface[]> = {};
    messages.forEach((msg) => {
        const dateKey = msg.props.model.date;
        if (!groups[dateKey]) groups[dateKey] = [];
        groups[dateKey].push(msg);
    });
    return groups;
};

export const parseFirestoreTimestamp = (timestamp: unknown): Date | null => {
    if (timestamp == null) return null;

    try {
        if (typeof (timestamp as { toDate?: () => Date }).toDate === "function") {
            const date = (timestamp as { toDate: () => Date }).toDate();
            return moment(date).isValid() ? date : null;
        }

        if (typeof (timestamp as { seconds?: number }).seconds === "number") {
            const date = new Date((timestamp as { seconds: number }).seconds * 1000);
            return moment(date).isValid() ? date : null;
        }

        if (timestamp instanceof Date) {
            return moment(timestamp).isValid() ? timestamp : null;
        }

        const parsed = moment(timestamp as string | number | Date);
        return parsed.isValid() ? parsed.toDate() : null;
    } catch {
        return null;
    }
};

export const getDayLabel = (dateIso: string) => {
    const m = moment(dateIso);
    if (!dateIso || !m.isValid()) return "Today";
    if (m.isSame(moment(), "day")) return "Today";
    if (m.isSame(moment().subtract(1, "day"), "day")) return "Yesterday";
    if (m.isAfter(moment().subtract(6, "days"), "day")) return m.format("dddd");
    return m.format("DD/MM/YYYY");
};

export interface UseCollaborationChatMessagesParams {
    entityChatRoom: EntityChatRoom | null;
    currentEntityId: string | null | undefined;
}

export function useCollaborationChatMessages({
    entityChatRoom,
    currentEntityId,
}: UseCollaborationChatMessagesParams) {
    const { firebaseUser, db, auth, appUser } = useFirebase();

    const [input, setInput] = useState("");
    const [participants, setParticipants] = useState<Record<string, any>>({});
    const [participantsIDs, setParticipantsIDs] = useState<string[]>([]);
    const [rawMessages, setRawMessages] = useState<any[]>([]);
    const [groupedMessages, setGroupedMessages] = useState<Record<string, MessageInterface[]>>({});


    useEffect(() => {
        if (!entityChatRoom || !firebaseUser || !entityChatRoom.uuid) return;
        const roomId = entityChatRoom.uuid;

        const q = query(
            collection(db, "chatRooms", roomId, "messages"),
            orderBy("timestamp", "asc")
        );

        const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
                const newMessages = snapshot.docs
                    .map((docSnap) => ({
                        id: docSnap.id,
                        ...docSnap.data(),
                    }))
                    .filter((m: any) => m.senderId);

                const senderIdsArray = newMessages.map((m: any) => m.senderId);
                const uniqueSenderIds: string[] = [];
                for (let i = 0; i < senderIdsArray.length; i++) {
                    if (uniqueSenderIds.indexOf(senderIdsArray[i]) === -1) {
                        uniqueSenderIds.push(senderIdsArray[i]);
                    }
                }
                setParticipantsIDs(uniqueSenderIds);
                setRawMessages(newMessages);
            },
            (error) => {
                console.error("Error subscribing to chat messages:", error);
            }
        );

        return () => unsubscribe();
    }, [entityChatRoom, firebaseUser, db]);

    useEffect(() => {
        if (participantsIDs.length === 0) return;

        const fetchParticipants = async () => {
            const usersRef = collection(db, "users");
            const idsToFetch = participantsIDs.filter((id) => !participants[id]);
            if (idsToFetch.length === 0) return;

            const batches = [];
            for (let i = 0; i < idsToFetch.length; i += 10) {
                batches.push(idsToFetch.slice(i, i + 10));
            }

            const newParticipants: Record<string, any> = {};
            for (const batch of batches) {
                const q = query(usersRef, where("__name__", "in", batch));
                const snapshot = await getDocs(q);
                snapshot.forEach((docSnap) => {
                    newParticipants[docSnap.id] = docSnap.data();
                });
            }

            if (Object.keys(newParticipants).length > 0) {
                setParticipants((prev) => ({ ...prev, ...newParticipants }));
            }
        };

        fetchParticipants();
    }, [participants, participantsIDs, db]);

    useEffect(() => {
        console.log('participants changed',participants);
    }, [participants])

    const transformMessages = (messages: any[]): Array<MessageInterface> => {
        const influencerUuid = entityChatRoom?.influencer?.uuid;
        const messagesWithKeys = messages.map((msg) => ({
            ...msg,
            __groupKey: getMessageGroupKey(msg, currentEntityId, influencerUuid),
        }));

        return messagesWithKeys.map((msg, i) => {
            const lastMsg = messagesWithKeys[i - 1];
            const nextMsg = messagesWithKeys[i + 1];
            const dateKey = getMessageDateKey(msg);
            const lastMsgSameDay =
                lastMsg && getMessageDateKey(lastMsg) === dateKey ? lastMsg : undefined;
            const nextMsgSameDay =
                nextMsg && getMessageDateKey(nextMsg) === dateKey ? nextMsg : undefined;
            const position = getMessagePosition(msg, lastMsgSameDay, nextMsgSameDay);
            const isInfluencerMessage = Boolean(
                influencerUuid && msg.senderEntityId === influencerUuid
            );
            const direction =
                msg.senderEntityId === currentEntityId || isInfluencerMessage
                    ? "outgoing"
                    : "incoming";
            const sender = participants[msg.senderId];
            const messageDate = parseFirestoreTimestamp(msg.timestamp) ?? new Date();
            const isGroupStart = ["single", "first"].includes(position);
            const senderDisplayName = isInfluencerMessage
                ? entityChatRoom?.influencer?.name ?? sender?.name ?? msg.senderId
                : msg.senderId === auth.currentUser?.uid
                    ? appUser?.name ?? sender?.name ?? msg.senderId
                    : sender?.name ?? msg.senderId;

            const children: React.ReactElement[] = [];

            if (direction === "incoming" && isGroupStart) {
                children.push(
                    <Avatar
                        key="avatar"
                        name={senderDisplayName}
                        style={{ height: 30, width: 30, minHeight: "30px", minWidth: "30px" }}
                        src={sender?.profile_photo_url ?? userDummyImg}
                    />
                );
            }

            const isOwnMessage = msg.senderId === auth.currentUser?.uid;

            if (isGroupStart && senderDisplayName && !isOwnMessage) {
                const influencerAvatarSrc =
                    entityChatRoom?.influencer?.profile_picture_url ??
                    sender?.profile_photo_url;

                children.push(
                    <Message.Header
                        key="header"
                        className={`chat-sender-group-name${isInfluencerMessage ? " chat-sender-group-name--influencer" : ""}`}
                    >
                        {isInfluencerMessage ? (
                            <span className="chat-sender-group-name__label">
                                {influencerAvatarSrc ? (
                                    <img
                                        src={influencerAvatarSrc}
                                        alt=""
                                        className="chat-sender-group-avatar"
                                    />
                                ) : (
                                    <span className="chat-sender-group-avatar chat-sender-group-avatar--initials">
                                        {(senderDisplayName || "?")
                                            .split(" ")
                                            .map((part: string) => part[0])
                                            .join("")
                                            .slice(0, 2)
                                            .toUpperCase()}
                                    </span>
                                )}
                                <span>{senderDisplayName}</span>
                            </span>
                        ) : (
                            senderDisplayName
                        )}
                    </Message.Header>
                );
            }

            return {
                type: "message",
                props: {
                    model: {
                        message: msg.text ?? "",
                        sentTime: moment(messageDate).format("h:mma"),
                        sender: senderDisplayName,
                        direction,
                        position,
                        senderId: msg.senderId,
                        date: moment(messageDate).startOf("day").toISOString(),
                        isOwnMessage,
                    },
                    ...(children.length > 0 ? { children } : {}),
                    ...((direction === "incoming" && ["normal", "last"].includes(position))
                        ? { avatarSpacer: true }
                        : {}),
                },
            };
        });
    };

    useEffect(() => {
        const transformed = transformMessages(rawMessages);
        setGroupedMessages(groupMessagesByDate(transformed));
    }, [
        participants,
        rawMessages,
        currentEntityId,
        auth.currentUser?.uid,
        appUser?.name,
        entityChatRoom?.influencer?.uuid,
        entityChatRoom?.influencer?.name,
    ]);

    const sendMessage = async () => {
        if (
            !input.trim() ||
            !auth.currentUser ||
            !entityChatRoom?.uuid ||
            !appUser ||
            entityChatRoom.access !== "w"
        ) {
            return;
        }

        const timestamp = serverTimestamp();
        const roomId = entityChatRoom.uuid;
        const message = {
            senderId: auth.currentUser.uid,
            text: input,
            timestamp,
            senderEntityId: currentEntityId,
        };
        await addDoc(collection(db, "chatRooms", roomId, "messages"), message);

        const lastMessage = {
            ...message,
            senderName: appUser.name,
            senderProfileUrl: appUser.profile_photo_url,
        };

        const chatRoomRef = doc(db, "chatRooms", roomId);
        updateDoc(chatRoomRef, { lastMessage });

        if (Array.isArray(entityChatRoom.entities)) {
            for (const entityId of entityChatRoom.entities) {
                const entityChatRef = doc(db, "entityChatRooms", entityId, "rooms", roomId);
                updateDoc(entityChatRef, {
                    lastMessage,
                    ...(entityId === currentEntityId
                        ? { unreadCount: 0 }
                        : { unreadCount: increment(1) }),
                });
            }
        }

        setInput("");
    };

    const markAsRead = async (): Promise<boolean> => {
        if (!entityChatRoom || !currentEntityId) return false;
        if ((entityChatRoom.unreadCount ?? 0) <= 0) return false;
        try {
            const roomId = entityChatRoom.uuid;
            if (!roomId) return false;
            const entityChatRef = doc(db, "entityChatRooms", currentEntityId, "rooms", roomId);
            await updateDoc(entityChatRef, { unreadCount: 0 });
            return true;
        } catch (error) {
            console.error("Failed to mark as read:", error);
            return false;
        }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
        e.preventDefault();

        const html = e.clipboardData.getData("text/html");
        const text = e.clipboardData.getData("text/plain");

        const cleanHTML = html
            ? DOMPurify.sanitize(html, { ALLOWED_TAGS: CHAT_ALLOWED_TAGS, ALLOWED_ATTR: CHAT_ALLOWED_ATTR })
            : text.replace(/\n/g, "<br>");

        setInput((prev) => prev + cleanHTML);
    };

    return {
        input,
        setInput,
        groupedMessages,
        sendMessage,
        markAsRead,
        handlePaste,
    };
}

export type CollaborationChatMessagesState = ReturnType<typeof useCollaborationChatMessages>;

export interface CollaborationChatMessagesProps {
    entityChatRoom: EntityChatRoom | null;
    messagesState: CollaborationChatMessagesState;
    loading?: boolean;
    showEmptyState?: boolean;
    dayLabelVariant?: "class" | "inline";
    messageInputClassName?: string;
}

const CollaborationChatMessages: React.FC<CollaborationChatMessagesProps> = ({
    entityChatRoom,
    messagesState,
    loading = false,
    showEmptyState = false,
    dayLabelVariant = "class",
    messageInputClassName,
}) => {
    const { input, setInput, groupedMessages, sendMessage, handlePaste, markAsRead } = messagesState;
    const chatRootRef = useRef<HTMLDivElement>(null);
    const markReadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isAtBottomRef = useRef(false);
    const hasUnread = (entityChatRoom?.unreadCount ?? 0) > 0;

    const clearMarkReadTimer = useCallback(() => {
        if (markReadTimerRef.current) {
            clearTimeout(markReadTimerRef.current);
            markReadTimerRef.current = null;
        }
    }, []);

    const scheduleMarkAsRead = useCallback(() => {
        if (!hasUnread || markReadTimerRef.current) return;

        markReadTimerRef.current = setTimeout(async () => {
            markReadTimerRef.current = null;
            const container = findMessageListScrollContainer(chatRootRef.current);
            if (!container || !isScrolledToBottom(container)) return;

            await markAsRead();
        }, MARK_AS_READ_DELAY_MS);
    }, [hasUnread, markAsRead]);

    const handleMessageListScroll = useCallback(() => {
        const container = findMessageListScrollContainer(chatRootRef.current);
        if (!container) return;

        const atBottom = isScrolledToBottom(container);

        if (atBottom && !isAtBottomRef.current) {
            isAtBottomRef.current = true;
            scheduleMarkAsRead();
        } else if (!atBottom && isAtBottomRef.current) {
            isAtBottomRef.current = false;
            clearMarkReadTimer();
        }
    }, [clearMarkReadTimer, scheduleMarkAsRead]);

    useEffect(() => {
        if (!entityChatRoom || !hasUnread) {
            isAtBottomRef.current = false;
            clearMarkReadTimer();
            return;
        }

        let container: HTMLElement | null = null;
        let rafId = 0;
        let rafAttempts = 0;
        const maxRafAttempts = 30;

        const attachScrollListener = () => {
            container = findMessageListScrollContainer(chatRootRef.current);
            if (!container && rafAttempts < maxRafAttempts) {
                rafAttempts += 1;
                rafId = requestAnimationFrame(attachScrollListener);
                return;
            }
            if (!container) return;

            container.addEventListener("scroll", handleMessageListScroll, { passive: true });

            if (isScrolledToBottom(container)) {
                isAtBottomRef.current = true;
                scheduleMarkAsRead();
            } else {
                isAtBottomRef.current = false;
            }
        };

        const attachTimeoutId = window.setTimeout(attachScrollListener, 0);

        return () => {
            window.clearTimeout(attachTimeoutId);
            cancelAnimationFrame(rafId);
            isAtBottomRef.current = false;
            if (container) {
                container.removeEventListener("scroll", handleMessageListScroll);
            }
            clearMarkReadTimer();
        };
    }, [
        entityChatRoom?.uuid,
        hasUnread,
        groupedMessages,
        loading,
        handleMessageListScroll,
        scheduleMarkAsRead,
        clearMarkReadTimer,
    ]);

    useEffect(() => () => clearMarkReadTimer(), [clearMarkReadTimer]);

    if (!entityChatRoom) {
        return null;
    }

    return (
        <div ref={chatRootRef} className="collaboration-chat-messages" style={{ display: "contents" }}>
        <MainContainer>
            <ChatContainer>
                <MessageList className="mb-2">
                    {showEmptyState && Object.keys(groupedMessages).length === 0 && !loading && (
                        <div className="d-flex h-100 w-100 justify-content-center align-items-center">
                            <div className="length-zero m-0">
                                <h3>Send a message to start the conversation</h3>
                            </div>
                        </div>
                    )}
                    {Object.keys(groupedMessages).map((dateKey) => (
                        <div key={dateKey}>
                            <div
                                className={
                                    dayLabelVariant === "class"
                                        ? "position-sticky text-center my-2 d-flex align-items-center justify-content-center chat-day-divider"
                                        : "position-sticky text-center my-2 d-flex align-items-center justify-content-center"
                                }
                                style={{ zIndex: 10, top: "1rem" }}
                            >
                                {dayLabelVariant === "class" ? (
                                    <div className="chat-day-label">{getDayLabel(dateKey)}</div>
                                ) : (
                                    <div
                                        style={{
                                            zIndex: 10,
                                            background: "#f8f9fa",
                                            borderRadius: "8px",
                                            display: "inline-block",
                                            padding: "3px 10px",
                                            fontSize: "12px",
                                            color: "#555",
                                        }}
                                    >
                                        {getDayLabel(dateKey)}
                                    </div>
                                )}
                            </div>

                            {groupedMessages[dateKey].map((msg, i) => {
                                const isOwnMessage = Boolean(msg.props.model.isOwnMessage);
                                const useIncomingBubbleStyle =
                                    msg.props.model.direction === "incoming" ||
                                    (msg.props.model.direction === "outgoing" && !isOwnMessage);
                                const contentStyleClass = useIncomingBubbleStyle ? "incoming" : "outgoing";

                                return (
                                <Message
                                    key={i}
                                    {...msg.props}
                                    className={
                                        msg.props.model.direction === "outgoing" && !isOwnMessage
                                            ? "chat-message-other-outgoing"
                                            : undefined
                                    }
                                >
                                    {msg.props.children}
                                    <Message.CustomContent>
                                        <Message.HtmlContent
                                            className={`message-html-content ${contentStyleClass}`}
                                            html={DOMPurify.sanitize(
                                                sanitizeURLs(msg.props?.model.message ?? ""),
                                                {
                                                    ALLOWED_TAGS: CHAT_ALLOWED_TAGS,
                                                    ALLOWED_ATTR: CHAT_ALLOWED_ATTR,
                                                }
                                            )}
                                        />
                                        <span className={`msg-sent-time ${contentStyleClass}`}>
                                            {msg.props.model.sentTime}
                                        </span>
                                    </Message.CustomContent>
                                </Message>
                                );
                            })}
                        </div>
                    ))}
                </MessageList>

                <MessageInput
                    placeholder={
                        entityChatRoom.access !== "w"
                            ? "You cannot send messages in this chat"
                            : "Type your message..."
                    }
                    value={input}
                    className={messageInputClassName}
                    onChange={(val) => setInput(val)}
                    onSend={sendMessage}
                    attachButton={false}
                    sendDisabled={entityChatRoom.access !== "w"}
                    disabled={entityChatRoom.access !== "w"}
                    onPaste={handlePaste}
                />
            </ChatContainer>
        </MainContainer>
        </div>
    );
};

export default CollaborationChatMessages;
